/**
 * Standard Object Publishing — shared status labels and Object Manager visibility rules.
 * Product Builder bypasses subscription allowlist, allow_explore, and field catalog gates.
 */
import { getEntitlementsSection, canAccessSetup } from './entitlements';

export const PRODUCT_BUILDER_FEATURE_KEY = 'product_builder';

export const STANDARD_STATUS = Object.freeze({
  DRAFT: 'draft',
  PUBLISHED: 'published',
  ARCHIVED: 'archived',
});

export const STANDARD_STATUS_LABELS = Object.freeze({
  [STANDARD_STATUS.DRAFT]: 'Draft Standard Object',
  [STANDARD_STATUS.PUBLISHED]: 'Published Standard Object',
  [STANDARD_STATUS.ARCHIVED]: 'Archived Standard Object',
});

/** Standard objects not listed separately in subscription allowlist (parent object gates visibility). */
const STANDARD_OBJECT_CHILD_TO_PARENT = Object.freeze({
  product_price: 'opportunity',
  opportunity_product: 'opportunity',
  order_product: 'order',
  quote_product: 'quote',
  task: 'activity',
});

export function normalizeStandardStatus(raw) {
  const status = String(raw ?? '').toLowerCase().trim();
  if (status === STANDARD_STATUS.DRAFT) return STANDARD_STATUS.DRAFT;
  if (status === STANDARD_STATUS.PUBLISHED) return STANDARD_STATUS.PUBLISHED;
  if (status === STANDARD_STATUS.ARCHIVED) return STANDARD_STATUS.ARCHIVED;
  return null;
}

/**
 * Whether the standard object publishing status label should appear in Object Manager.
 * Published (and archived) statuses are product-builder-only; draft is visible to all.
 */
export function shouldShowStandardObjectStatusLabel(object, productBuilderMode = false) {
  if (!object?.is_standard) return false;
  const status = normalizeStandardStatus(object.standard_status);
  if (!status) return false;
  if (
    status === STANDARD_STATUS.PUBLISHED
    || status === STANDARD_STATUS.ARCHIVED
  ) {
    return productBuilderMode === true;
  }
  return true;
}

/** Label for standard objects only; null for custom objects or unknown status. */
export function getStandardObjectStatusLabel(object, productBuilderMode = false) {
  if (!shouldShowStandardObjectStatusLabel(object, productBuilderMode)) return null;
  const status = normalizeStandardStatus(object.standard_status);
  return STANDARD_STATUS_LABELS[status] || null;
}

export function isDraftStandardObject(object) {
  return object?.is_standard === true
    && normalizeStandardStatus(object.standard_status) === STANDARD_STATUS.DRAFT;
}

/** Draft standard object in product builder can be published via catalog wizard. */
export function canPublishStandardObject(object, productBuilderMode) {
  return productBuilderMode === true && isDraftStandardObject(object);
}

/** Published/archived standard object catalog can be edited in product builder. */
export function canEditStandardObjectCatalog(object, productBuilderMode) {
  if (productBuilderMode !== true || !object?.is_standard) return false;
  const status = normalizeStandardStatus(object.standard_status);
  return status === STANDARD_STATUS.PUBLISHED || status === STANDARD_STATUS.ARCHIVED;
}

export function canManageStandardObjectCatalog(object, productBuilderMode) {
  return canPublishStandardObject(object, productBuilderMode)
    || canEditStandardObjectCatalog(object, productBuilderMode);
}

/**
 * When true, subscription allowlist filtering is skipped (product builder: all objects; otherwise draft only).
 */
export function shouldBypassStandardObjectAllowlist(object, productBuilderMode) {
  if (productBuilderMode === true) return true;
  return isDraftStandardObject(object);
}

/** Product builder orgs bypass allow_explore and subscription catalog gates in Object Manager. */
export function shouldBypassStandardObjectCatalogFilters(productBuilderMode) {
  return productBuilderMode === true;
}

/** Resolve allow_explore from config, with schema/tenant override. */
export function getEffectiveAllowExplore(config, schemaOrTenantKey) {
  if (!config || typeof config !== 'object') return true;
  const base = config?.allow_explore;
  const override =
    schemaOrTenantKey &&
      config[schemaOrTenantKey] &&
      typeof config[schemaOrTenantKey] === 'object' &&
      Object.prototype.hasOwnProperty.call(config[schemaOrTenantKey], 'allow_explore')
      ? config[schemaOrTenantKey].allow_explore
      : undefined;
  const value = override !== undefined ? override : base;
  return !(value === false || value === 'false' || value === 0 || value === '0');
}

const parseObjectConfig = (rawConfig) => {
  if (!rawConfig) return {};
  if (typeof rawConfig === 'string') {
    try {
      const parsed = JSON.parse(rawConfig);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }
  return typeof rawConfig === 'object' ? rawConfig : {};
};

/**
 * Subscription allowlist gate for standard objects (custom objects always pass).
 * Draft standard objects bypass when productBuilderMode is true.
 */
export function isStandardObjectAllowedBySubscription(object, options = {}) {
  const {
    allowedStandardObjects,
    restrictStandardObjects,
    productBuilderMode = false,
  } = options;

  const nameLc = String(object?.object_name || '').toLowerCase();

  if (nameLc === 'user' && canAccessSetup()) {
    return true;
  }

  if (shouldBypassStandardObjectAllowlist(object, productBuilderMode)) {
    return true;
  }

  if (!restrictStandardObjects) {
    return true;
  }

  if (!object?.is_standard) {
    return true;
  }

  const parent = STANDARD_OBJECT_CHILD_TO_PARENT[nameLc];
  if (parent) {
    return allowedStandardObjects.has(String(parent).toLowerCase());
  }

  return allowedStandardObjects.has(nameLc);
}

/**
 * Object Manager list filtering: deleted excluded, subscription allowlist, allow_explore.
 * @param {Array<object>} objectsWithFields
 * @param {{ productBuilderMode?: boolean, schemaOrTenantKey?: string }} [options]
 */
export function filterObjectManagerObjects(objectsWithFields, options = {}) {
  if (!Array.isArray(objectsWithFields)) {
    return [];
  }

  const objectManagerConfig = getEntitlementsSection('objectManager', {});
  const allowedStandardObjects = new Set(
    (objectManagerConfig?.allowedObjects || []).map((name) => String(name).toLowerCase())
  );
  const restrictStandardObjects =
    objectManagerConfig?.mode === 'allowlist'
    && objectManagerConfig?.scope === 'standard'
    && allowedStandardObjects.size > 0;

  const schemaOrTenantKey = options.schemaOrTenantKey
    ?? (typeof localStorage !== 'undefined' ? localStorage.getItem('tenantcode') || undefined : undefined);

  const productBuilderMode = options.productBuilderMode === true;
  const bypassCatalogFilters = shouldBypassStandardObjectCatalogFilters(productBuilderMode);

  const canExploreObject = (obj) => {
    if (bypassCatalogFilters) return true;
    const config = parseObjectConfig(obj?.config);
    return getEffectiveAllowExplore(config, schemaOrTenantKey);
  };

  return objectsWithFields
    .filter((obj) => !obj.is_deleted)
    .filter((obj) => (
      bypassCatalogFilters
      || isStandardObjectAllowedBySubscription(obj, {
        allowedStandardObjects,
        restrictStandardObjects,
        productBuilderMode,
      })
    ))
    .filter(canExploreObject);
}

/**
 * Workflow "Select Object" dropdown: same gates as Object Manager plus allow_workflow on standard objects.
 * Product builder bypasses subscription allowlist, allow_explore, and allow_workflow.
 * @param {Array<object>} rawList
 * @param {{ productBuilderMode?: boolean, schemaOrTenantKey?: string }} [options]
 */
export function filterWorkflowAllowedObjects(rawList, options = {}) {
  if (!Array.isArray(rawList)) {
    return [];
  }

  const objectManagerConfig = getEntitlementsSection('objectManager', {});
  const standardObjectConfigs = getEntitlementsSection('standardObjectConfigs', {}) || {};
  const allowedStandardObjects = new Set(
    (objectManagerConfig?.allowedObjects || []).map((name) => String(name).toLowerCase())
  );
  const restrictStandardObjects =
    objectManagerConfig?.mode === 'allowlist'
    && objectManagerConfig?.scope === 'standard'
    && allowedStandardObjects.size > 0;

  const schemaOrTenantKey = options.schemaOrTenantKey
    ?? (typeof localStorage !== 'undefined' ? localStorage.getItem('tenantcode') || undefined : undefined);

  const productBuilderMode = options.productBuilderMode === true;
  const bypassCatalogFilters = shouldBypassStandardObjectCatalogFilters(productBuilderMode);

  const getStandardConfig = (obj) => {
    if (obj?.is_standard === false) return null;
    const name = String(obj?.object_name || '').toLowerCase();
    return standardObjectConfigs[obj?.object_name] ?? standardObjectConfigs[name] ?? null;
  };

  const canExploreObject = (obj) => {
    if (bypassCatalogFilters) return true;
    const stdConfig = getStandardConfig(obj);
    if (stdConfig) {
      return getEffectiveAllowExplore(stdConfig, schemaOrTenantKey);
    }
    const config = parseObjectConfig(obj?.config);
    return getEffectiveAllowExplore(config, schemaOrTenantKey);
  };

  const allowWorkflow = (obj) => {
    if (bypassCatalogFilters) return true;
    const stdConfig = getStandardConfig(obj);
    if (stdConfig) {
      const v = stdConfig.allow_workflow;
      if (v === false || v === 'false' || v === 0 || v === '0') return false;
    }
    return true;
  };

  return rawList
    .filter((obj) => !obj.is_deleted)
    .filter((obj) => (
      bypassCatalogFilters
      || isStandardObjectAllowedBySubscription(obj, {
        allowedStandardObjects,
        restrictStandardObjects,
        productBuilderMode,
      })
    ))
    .filter(canExploreObject)
    .filter(allowWorkflow);
}
